/**
 * Title Utilities for Artefact Export
 *
 * Provides smart title detection to avoid duplicate titles in exported documents.
 * Handles both markdown headings (# Title) and plain text first lines.
 */

/**
 * Check if markdown content starts with a heading that matches the title
 * Handles both markdown headings (# Title) and plain text first lines
 *
 * @param content - The markdown content to check
 * @param title - The title to compare against
 * @returns true if content starts with a heading matching the title
 */
export const contentStartsWithTitle = (content: string, title: string): boolean => {
  if (!content || !title) return false;

  const firstLine = content.trim().split("\n")[0];
  // Remove markdown heading prefix (# ## ### etc.)
  const normalisedFirstLine = firstLine
    .replace(/^#+\s*/, "")
    .trim()
    .toLowerCase();
  const normalisedTitle = title.trim().toLowerCase();

  return normalisedFirstLine === normalisedTitle;
};

/**
 * Prepare markdown content with title, avoiding duplicates
 * Only prepends the title if the content doesn't already start with it
 *
 * @param content - The markdown content
 * @param title - The title to potentially prepend
 * @returns The content with title prepended (if not already present)
 */
export const prepareMarkdownWithTitle = (content: string, title: string): string => {
  if (contentStartsWithTitle(content, title)) {
    return content;
  }

  return `# ${title}\n\n${content}`;
};
